import { useCallback, useEffect, useRef, useState } from 'react';
import { useBrand } from '../lib/brand';
import { activeHeroCaption, parseHeroVtt, type HeroCaptionCue } from '../lib/heroCaptions';

const HERO_FILM_SRC = '/brand/marshal/hero-promo.mp4?v=5';
const HERO_CAPTIONS_SRC = '/brand/marshal/hero-promo.en.vtt';
const POSTER = '/marketing/sessions-1440.webp';

function primeHeroVideo(node: HTMLVideoElement) {
  node.defaultMuted = true;
  node.muted = true;
  node.setAttribute('muted', '');
  node.setAttribute('playsinline', '');
  node.setAttribute('webkit-playsinline', 'true');
}

export function requestFilmPlay() {
  window.dispatchEvent(new Event('marshal:play-film'));
}

export function FilmBand() {
  const brand = useBrand();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cuesRef = useRef<HeroCaptionCue[]>([]);
  const [muted, setMuted] = useState(true);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [caption, setCaption] = useState('');
  const [near, setNear] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const rootRef = useRef<HTMLElement | null>(null);

  const setVideoNode = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    if (node) primeHeroVideo(node);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setNear(true);
      },
      { rootMargin: '240px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!near) return;
    let cancelled = false;
    fetch(HERO_CAPTIONS_SRC)
      .then((res) => (res.ok ? res.text() : ''))
      .then((text) => {
        if (cancelled || !text) return;
        cuesRef.current = parseHeroVtt(text);
        const el = videoRef.current;
        if (el) setCaption(activeHeroCaption(cuesRef.current, el.currentTime));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [near]);

  const playMuted = useCallback(() => {
    const el = videoRef.current;
    if (!el || failed) return;
    primeHeroVideo(el);
    setMuted(true);
    const pending = el.play();
    if (pending && typeof pending.then === 'function') {
      pending.then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }, [failed]);

  useEffect(() => {
    if (!near || failed || reduceMotion) return;
    playMuted();
  }, [near, failed, reduceMotion, playMuted]);

  useEffect(() => {
    const onAsk = () => playMuted();
    window.addEventListener('marshal:play-film', onAsk);
    return () => window.removeEventListener('marshal:play-film', onAsk);
  }, [playMuted]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || failed || !near) return;
    const hideNative = () => {
      for (let i = 0; i < el.textTracks.length; i++) {
        const track = el.textTracks[i];
        if (track && track.mode !== 'hidden') track.mode = 'hidden';
      }
    };
    const sync = () => {
      const next = activeHeroCaption(cuesRef.current, el.currentTime);
      setCaption((current) => (current === next ? current : next));
    };
    hideNative();
    el.addEventListener('loadedmetadata', hideNative);
    el.textTracks.addEventListener('change', hideNative);
    el.addEventListener('timeupdate', sync);
    el.addEventListener('seeked', sync);
    el.addEventListener('play', () => setPlaying(true));
    el.addEventListener('pause', () => setPlaying(false));
    return () => {
      el.removeEventListener('loadedmetadata', hideNative);
      el.textTracks.removeEventListener('change', hideNative);
      el.removeEventListener('timeupdate', sync);
      el.removeEventListener('seeked', sync);
    };
  }, [failed, near]);

  function togglePlay() {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      primeHeroVideo(el);
      const pending = el.play();
      if (pending) pending.then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      el.pause();
      setPlaying(false);
    }
  }

  function toggleSound() {
    const el = videoRef.current;
    if (!el) return;
    const nextMuted = !el.muted;
    el.muted = nextMuted;
    setMuted(nextMuted);
    if (el.paused) {
      const pending = el.play();
      if (pending) pending.then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }

  return (
    <section id="film" ref={rootRef} data-mkt-surface="dark" className="scroll-mt-20 bg-black text-white">
      <div className="mx-auto max-w-[1100px] px-5 py-16 md:py-24">
        <h2 className="text-center text-mkt-section-sm md:text-mkt-section">Watch the film.</h2>
        <p className="mx-auto mt-4 max-w-copy text-center text-balance text-mkt-sub-sm text-white/80 md:text-mkt-sub">
          A short look at a club morning, from the first enquiry to book-in on a phone.
        </p>
        <div className="relative mt-10 overflow-hidden rounded-2xl bg-black">
          {failed ? (
            <img src={POSTER} alt="" className="aspect-video w-full object-cover" />
          ) : (
            <video
              ref={setVideoNode}
              className="aspect-video w-full bg-black object-contain"
              autoPlay={near && !reduceMotion}
              muted
              loop
              playsInline
              poster={POSTER}
              preload={near ? 'metadata' : 'none'}
              aria-label={`${brand.name} product film`}
              onError={() => setFailed(true)}
            >
              {near ? <source src={HERO_FILM_SRC} type="video/mp4" /> : null}
              <track kind="captions" src={HERO_CAPTIONS_SRC} srcLang="en" label="English" />
            </video>
          )}
          {caption ? (
            <div className="pointer-events-none absolute inset-x-4 bottom-4 flex justify-center" aria-live="polite">
              <p className="max-w-xl whitespace-pre-line rounded-lg bg-black/80 px-3 py-1.5 text-center text-[15px] leading-snug text-white">
                {caption}
              </p>
            </div>
          ) : null}
        </div>
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={togglePlay}
            className="inline-flex h-11 items-center rounded-full bg-white px-5 text-[15px] font-medium text-black hover:bg-white/90"
          >
            {playing ? 'Pause' : 'Play'}
          </button>
          <button
            type="button"
            onClick={toggleSound}
            className="inline-flex h-11 items-center rounded-full bg-white/15 px-5 text-[15px] font-medium text-white ring-1 ring-white/50 hover:bg-white/25"
            aria-pressed={!muted}
          >
            {muted ? 'Unmute' : 'Mute'}
          </button>
        </div>
      </div>
    </section>
  );
}
